import {
  onCleanup,
  batch,
  createEffect,
  createRoot,
  createRenderEffect,
  onMount,
  on,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import {
  register,
  createContext,
  withProvider,
  withConsumer,
  compose,
} from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';
import { animate, spring } from 'https://cdn.jsdelivr.net/npm/motion@10.16.2/+esm';
import { enableBodyScroll, disableBodyScroll } from 'https://cdn.jsdelivr.net/npm/body-scroll-lock@4.0.0-beta.0/+esm';
import { createFocusTrap } from 'https://cdn.jsdelivr.net/npm/focus-trap@7.5.2/+esm';
function getContext(element) {
  const elementContextSymbol = Object.getOwnPropertySymbols(element).find((s) => s.description === 'element-context');
  const contextSymbol = Object.getOwnPropertySymbols(element[elementContextSymbol]).find(
    (s) => s.description === 'context'
  );
  return element[elementContextSymbol][contextSymbol];
}

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

const ModalContextState = createContext((state = { isOpen: false, isAnimating: false, animationQueue: [] }) => {
  return createStore(state);
});

function hideElement(element) {
  element.style.display = 'none';
}

function showElement(element) {
  element.style.display = 'block';
}

function updateAnimationQueue(setter, value) {
  return setter('animationQueue', (state) => [...state, value]);
}

function getSpringConfig(element) {
  const style = window.getComputedStyle(element);
  return {
    ...getTransitionConfig(element),
    easing: {
      damping: parseFloat(style.getPropertyValue('--damping')) || 10,
      velocity: parseFloat(style.getPropertyValue('--velocity')) || 0,
      stiffness: parseFloat(style.getPropertyValue('--stiffness')) || 100,
      mass: parseFloat(style.getPropertyValue('--mass')) || 2,
      restSpeed: parseFloat(style.getPropertyValue('--rest-speed')) || 2,
      restDistance: parseFloat(style.getPropertyValue('--rest-distance')) || 0.5,
    },
  };
}

function getTransitionConfig(element) {
  const style = window.getComputedStyle(element);
  return {
    duration: parseFloat(style.getPropertyValue('--duration')) || 0.3,
    delay: parseFloat(style.getPropertyValue('--delay')) || 0,
    endDelay: parseFloat(style.getPropertyValue('--end-delay')) || 0,
    easing: style.getPropertyValue('--easing') || 'ease',
    direction: style.getPropertyValue('--direction') || 'normal',
    repeat: parseInt(style.getPropertyValue('--repeat')) || 0,
    allowWebkitAcceleration: style.getPropertyValue('--webkit-acceleration') === 'true' || false,
  };
}

function ModalContext(props, { element }) {
  const [state, setState] = getContext(element);
  const focusTrap = createFocusTrap(element, {
    allowOutsideClick: true,
    escapeDeactivates: true,
    onDeactivate() {
      setState('isOpen', false);
      enableBodyScroll(element);
    },
  });
  const scrollableElements = element.querySelectorAll('.overflow-y-auto');

  onMount(() => {
    setState('isOpen', props.isOpen);
    setState('id', props.id);
  });

  createEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        element.setAttribute('is-open', isOpen);
      }
    )
  );

  createEffect(
    on(
      () => state.isAnimating,
      (isAnimating) => element.setAttribute('is-animating', isAnimating)
    )
  );

  createEffect(
    on(
      () => props.id,
      (id) => setState('id', id)
    )
  );

  createEffect(
    on(
      () => ({ openAfterDelay: props.openAfterDelay, delay: props.delay }),
      ({ openAfterDelay, delay }) => {
        if (!openAfterDelay || !delay) return;

        const id = setTimeout(() => {
          setState('isOpen', true);
        }, delay);

        return onCleanup(() => {
          clearTimeout(id);
        });
      }
    )
  );

  createEffect(
    on(
      () => state.animationQueue,
      (animationQueue) => {
        if (!animationQueue.length) return;
        const animations = Promise.all([...state.animationQueue]);
        setState('isAnimating', true);
        animations.then(() => {
          batch(() => {
            setState('isAnimating', false);
            setState('animationQueue', []);
          });
        });
      }
    )
  );

  createEffect(
    on(
      () => ({ isOpen: state.isOpen, animationQueue: state.animationQueue }),
      ({ isOpen, animationQueue }) => {
        if (!isOpen)
          return Promise.all(animationQueue).then(() => {
            scrollableElements.forEach((elem) => {
              elem.scrollTop = 0;
            });
            hideElement(element);
          });
      }
    )
  );

  createEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen) return;
        showElement(element);
        disableBodyScroll(element);
        focusTrap.activate();
        return onCleanup(focusTrap.deactivate);
      }
    )
  );

  createEffect(
    on(
      () => props.id,
      (id) => {
        const handleEvent = (event) => {
          switch (event.detail.action) {
            case 'open':
              return setState('isOpen', true);
            case 'close':
              return setState('isOpen', false);
            default:
              event.preventDefault();
              return setState('isOpen', (s) => !s);
          }
        };

        addEventListener(`modal.${id}`, handleEvent);

        return onCleanup(() => {
          removeEventListener(`modal.${id}`, handleEvent);
        });
      }
    )
  );
}

function ModalBackdrop(props, { element, modal }) {
  if (!modal) return;
  const [state, setState] = modal;
  const [firstRender, setFirstRender] = createSignal(true);

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen || firstRender()) return;

        const cleanup = enter(element);
        updateAnimationQueue(setState, cleanup.finished);
        return onCleanup(cleanup.finish);
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (isOpen || firstRender()) return;

        const cleanup = exit(element);
        updateAnimationQueue(setState, cleanup.finished);
        return onCleanup(cleanup.finish);
      }
    )
  );

  onMount(() => {
    setFirstRender(false);

    function closeModal() {
      setState('isOpen', false);
    }

    element.addEventListener('click', closeModal);

    return onCleanup(() => {
      element.removeEventListener('click', closeModal);
    });
  });

  function enter(element) {
    const transition = getTransitionConfig(element);
    return animate(
      element,
      {
        opacity: [`var(--opacity-from)`, `var(--opacity-to)`],
      },
      transition
    );
  }

  function exit(element) {
    const transition = getTransitionConfig(element);
    return animate(
      element,
      {
        opacity: [`var(--opacity-to)`, `var(--opacity-from)`],
      },
      transition
    );
  }
}

function ModalPanel(props, { element, modal }) {
  if (!modal) return;
  const [state, setState] = modal;
  const [firstRender, setFirstRender] = createSignal(true);

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen || firstRender()) return;

        const cleanup = enter(element);
        updateAnimationQueue(setState, cleanup.finished);
        return onCleanup(cleanup.finish);
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (isOpen || firstRender()) return;

        const cleanup = exit(element);
        updateAnimationQueue(setState, cleanup.finished);
        return onCleanup(cleanup.finish);
      }
    )
  );

  onMount(() => {
    setFirstRender(false);
  });

  function enter(element) {
    const { position, easing, ...rest } = getSpringConfig(element);

    return animate(
      element,
      {
        transform: [
          `translateX(var(--slide-x-from)) translateY(var(--slide-y-from)) scaleX(var(--scale-x-from)) scaleY(var(--scale-y-from))`,
          `translateX(var(--slide-x-to)) translateY(var(--slide-y-to)) scaleX(var(--scale-x-to)) scaleY(var(--scale-y-to))`,
        ],
        opacity: [`var(--opacity-from)`, `var(--opacity-to)`],
      },
      { ...rest, easing: spring(easing) }
    );
  }

  function exit(element) {
    const { position, easing, ...rest } = getSpringConfig(element);
    return animate(
      element,
      {
        transform: [
          `translateX(var(--slide-x-to)) translateY(var(--slide-y-to)) scaleX(var(--scale-x-to)) scaleY(var(--scale-y-to))`,
          `translateX(var(--slide-x-from)) translateY(var(--slide-y-from)) scaleX(var(--scale-x-from)) scaleY(var(--scale-y-from))`,
        ],
        opacity: [`var(--opacity-to)`, `var(--opacity-from)`],
      },
      { ...rest, easing: spring(easing) }
    );
  }
}

function ModalTrigger(props, { element }) {
  const dispatchAction = (action) => {
    const customEvent = new CustomEvent(`modal.${props.target.replace('#', '')}`, {
      detail: {
        action,
        currentTarget: element.target,
        relatedTarget: document.getElementById(props.target),
      },
    });
    dispatchEvent(customEvent);
  };

  createEffect(
    on(
      () => ({ action: props.action, on: props.on, shouldPreventDefault: props.preventDefault }),
      ({ action, on, shouldPreventDefault }) => {
        if (!on || !action) return;

        switch (on) {
          case 'enter':
            return handleOnEnter(action);
          case 'exit':
            return handleOnExit(action);
          default: {
            function dispatch(e) {
              if (shouldPreventDefault) e.preventDefault();
              return dispatchAction(action);
            }
            element.addEventListener(on, dispatch);

            return onCleanup(() => {
              element.removeEventListener(on, dispatch);
            });
          }
        }
      }
    )
  );

  // Handles the enter/exit events for the drawer.
  function handleOnEnter(action) {
    let unsubscribe = null;
    if (action === 'toggle') {
      unsubscribe = observeElementInViewport(
        element,
        () => dispatchAction('open'),
        () => dispatchAction('close')
      );
    } else {
      unsubscribe = observeElementInViewport(element, dispatch, dispatch);
    }

    function dispatch() {
      return dispatchAction(action);
    }

    return onCleanup(unsubscribe);
  }

  function handleOnExit(action) {
    let unsubscribe = null;
    if (action === 'toggle') {
      unsubscribe = observeElementInViewport(
        element,
        () => dispatchAction('close'),
        () => dispatchAction('open')
      );
    } else {
      unsubscribe = observeElementInViewport(element, dispatch, dispatch);
    }

    function dispatch() {
      return dispatchAction(action);
    }

    return onCleanup(unsubscribe);
  }
}

customShadowlessElement(
  'modal-context',
  {
    isOpen: false,
    isAnimating: false,
    id: '',
    openAfterDelay: false,
    delay: 0,
  },
  ModalContext,
  withProvider(ModalContextState)
);
customShadowlessElement('modal-backdrop', {}, ModalBackdrop, withConsumer(ModalContextState, 'modal'));
customShadowlessElement('modal-panel', {}, ModalPanel, withConsumer(ModalContextState, 'modal'));
customShadowlessElement('modal-trigger', { target: '', action: '', on: 'click', preventDefault: false }, ModalTrigger);
